import { Component, Input, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { ModalService } from '../../../../shared/services/modal.service';

export interface ExpenseItem {
  icon: string;
  label: string;
  amount: string;
  duration: string;
  color?: string; // optional custom color (default red for overspend)
}

@Component({
  selector: 'app-expense-tracker',
  standalone: true,
  imports: [CommonModule, CdkOverlayOrigin, WidgetEdit],
  templateUrl: './expense-tracker.html',
  styleUrl: './expense-tracker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpenseTracker {
  @Input() title = 'EXPENSE TRACKER';
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
  @Input() index = 0;
  @Input() isEditMode = false;
  @Input() isAddMode = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;

  constructor(private modalService: ModalService) { }

  openModal() {
    if (!this.isEditMode && !this.isAddMode) {
      this.modalService.openExpenseTrackerModal();
    }
  }

  currentIndex = 0;

  expenses: ExpenseItem[] = [
    { icon: 'assets/icons/img_umbrella.svg', label: 'Travel', amount: '₹5,700,425.35', duration: '1 Month', color: '#ef4d6c' },
    { icon: 'assets/icons/img_shopping-basket.svg', label: 'Food', amount: '₹5,700,425.35', duration: '1 Month' },
    { icon: 'assets/icons/img_umbrella.svg', label: 'Housing', amount: '₹1,200,000.00', duration: '1 Month' },
    { icon: 'assets/icons/img_shopping-basket.svg', label: 'Utilities', amount: '₹450,425.00', duration: '1 Month' },
    { icon: 'assets/icons/img_umbrella.svg', label: 'Insurance', amount: '₹123,456.00', duration: '1 Year' },
    { icon: 'assets/icons/img_shopping-basket.svg', label: 'Healthcare', amount: '₹890,123.00', duration: '1 Month' },
    { icon: 'assets/icons/img_umbrella.svg', label: 'Entertainment', amount: '₹50,000.00', duration: '1 Month' },
    { icon: 'assets/icons/img_shopping-basket.svg', label: 'Education', amount: '₹1,000,000.00', duration: '1 Year' },
  ];

  get visibleItemsCount(): number {
    switch (this.size) {
      case 'small': return 1;
      case 'medium': return 2;
      case 'half': return 3; // 'large size 3 record' - assuming half width is large relative to small/med
      case 'full': return 6; // 'extra lage time 6 recors' - assuming full width
      default: return 2;
    }
  }

  get visibleExpenses(): ExpenseItem[] {
    return this.expenses.slice(this.currentIndex, this.currentIndex + this.visibleItemsCount);
  }

  next() {
    if (this.isAddMode) return;
    if (this.currentIndex + this.visibleItemsCount < this.expenses.length) {
      this.currentIndex += this.visibleItemsCount;
    }
  }

  prev() {
    if (this.isAddMode) return;
    if (this.currentIndex - this.visibleItemsCount >= 0) {
      this.currentIndex -= this.visibleItemsCount;
    } else {
      this.currentIndex = 0;
    }
  }

  get paginationText(): string {
    const end = Math.min(this.currentIndex + this.visibleItemsCount, this.expenses.length);
    if (this.visibleItemsCount === 1) {
      return `${this.currentIndex + 1} / ${this.expenses.length}`;
    }
    return `${this.currentIndex + 1}-${end} / ${this.expenses.length}`;
  }

  get canGoNext(): boolean {
    return this.currentIndex + this.visibleItemsCount < this.expenses.length;
  }

  get canGoPrev(): boolean {
    return this.currentIndex > 0;
  }
}
